// src/operator/console/settings-console.tsx
//
// Page Réglages opérateur : profil utilisateur, changement de mot de passe
// et gestion 2FA (TOTP). S'appuie sur les endpoints BetterAuth standards
// exposés par /api/auth/* (plugins twoFactor, magicLink, admin, bearer).

import { createSignal, createResource, For, Show } from "solid-js";

interface SessionUser {
  id: string;
  email?: string;
  name?: string;
  roles?: string[];
  tenantId?: string | null;
  provider?: string;
  twoFactorEnabled?: boolean;
  [k: string]: unknown;
}
interface SessionResp {
  user?: SessionUser | null;
  session?: { provider?: string; [k: string]: unknown } | null;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend(url: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await r.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      data = { raw: text };
    }
  }
  if (!r.ok) {
    const msg =
      (data?.error as string) ||
      ((data?.message as string) ?? "") ||
      (typeof data?.raw === "string" ? (data.raw as string) : "") ||
      `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return data;
}

const inputStyle = {
  background: "var(--bg-2, #1e293b)",
  color: "var(--text-1, #e8ecf3)",
  border: "1px solid var(--border, #334155)",
  "border-radius": "4px",
  padding: "6px 10px",
  "font-size": "13px",
  "min-width": "240px",
} as const;

const labelStyle = {
  display: "block",
  "font-size": "11px",
  color: "var(--text-3, #94a3b8)",
  "margin-bottom": "3px",
  "text-transform": "uppercase",
  "letter-spacing": "0.5px",
} as const;

const sectionTitleStyle = {
  padding: "10px 12px",
  "font-weight": "600",
  color: "var(--text-2, #cbd5e1)",
  "border-bottom": "1px solid var(--border, #334155)",
  "font-size": "13px",
} as const;

const bannerError = {
  background: "rgba(220,38,38,0.12)",
  border: "1px solid var(--danger, #dc2626)",
  color: "var(--danger, #f87171)",
  padding: "8px 12px",
  "border-radius": "4px",
  "font-size": "12px",
  "margin-bottom": "12px",
} as const;

const bannerOk = {
  background: "rgba(34,197,94,0.10)",
  border: "1px solid var(--ok, #22c55e)",
  color: "var(--ok, #22c55e)",
  padding: "8px 12px",
  "border-radius": "4px",
  "font-size": "12px",
  "margin-bottom": "12px",
} as const;

const kvRow = {
  display: "flex",
  gap: "10px",
  padding: "6px 12px",
  "font-size": "13px",
  "align-items": "center",
} as const;

const kvKey = {
  color: "var(--text-3, #94a3b8)",
  "font-size": "11px",
  "text-transform": "uppercase",
  "letter-spacing": "0.5px",
  "min-width": "110px",
} as const;

export function SettingsConsolePage() {
  const [err, setErr] = createSignal<string | null>(null);
  const [ok, setOk] = createSignal<string | null>(null);

  // ── Session courante ─────────────────────────────────────────────
  const [session, { refetch: refetchSession }] = createResource<SessionResp>(async () => {
    try {
      return await jget<SessionResp>("/api/auth/get-session");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return {} as SessionResp;
    }
  });

  function notify(error: string | null, success: string | null) {
    setErr(error);
    setOk(success);
    if (success) {
      setTimeout(() => {
        if (ok() === success) setOk(null);
      }, 4000);
    }
  }

  async function signOut() {
    setErr(null);
    setOk(null);
    try {
      await jsend("/api/auth/sign-out");
    } catch (e) {
      // on poursuit quand même la redirection vers /login
      setErr(e instanceof Error ? e.message : String(e));
    }
    window.location.href = "/login";
  }

  // ── Changement de mot de passe ───────────────────────────────────
  const [pwdCurrent, setPwdCurrent] = createSignal("");
  const [pwdNew, setPwdNew] = createSignal("");
  const [pwdConfirm, setPwdConfirm] = createSignal("");
  const [pwdRevoke, setPwdRevoke] = createSignal(false);
  const [pwdBusy, setPwdBusy] = createSignal(false);

  async function submitChangePassword() {
    setErr(null);
    setOk(null);
    if (pwdNew().length < 8) {
      notify("Le nouveau mot de passe doit faire au moins 8 caractères.", null);
      return;
    }
    if (pwdNew() !== pwdConfirm()) {
      notify("La confirmation du mot de passe ne correspond pas.", null);
      return;
    }
    if (!pwdCurrent()) {
      notify("Mot de passe actuel requis.", null);
      return;
    }
    setPwdBusy(true);
    try {
      await jsend("/api/auth/change-password", {
        currentPassword: pwdCurrent(),
        newPassword: pwdNew(),
        revokeOtherSessions: pwdRevoke(),
      });
      setPwdCurrent("");
      setPwdNew("");
      setPwdConfirm("");
      setPwdRevoke(false);
      notify(null, "Mot de passe modifié avec succès.");
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), null);
    } finally {
      setPwdBusy(false);
    }
  }

  // ── 2FA ──────────────────────────────────────────────────────────
  const [twoFAPassword, setTwoFAPassword] = createSignal("");
  const [twoFABusy, setTwoFABusy] = createSignal(false);
  const [twoFASetup, setTwoFASetup] = createSignal<{
    totpURI?: string;
    secret?: string;
    backupCodes?: string[];
  } | null>(null);
  const [twoFACode, setTwoFACode] = createSignal("");
  const [twoFARaw, setTwoFARaw] = createSignal<string | null>(null);

  async function enable2FA() {
    setErr(null);
    setOk(null);
    if (!twoFAPassword()) {
      notify("Mot de passe requis pour activer la 2FA.", null);
      return;
    }
    setTwoFABusy(true);
    try {
      const r = await jsend("/api/auth/two-factor/enable", { password: twoFAPassword() });
      // BetterAuth renvoie soit { totpURI, backupCodes } soit { data: { ... } }
      const payload = (r?.data && typeof r.data === "object" ? (r.data as Record<string, unknown>) : r) || {};
      const totpURI = (payload.totpURI as string) || (payload.uri as string) || undefined;
      const secret =
        (payload.secret as string) ||
        (totpURI ? new URL(totpURI.replace(/^otpauth:/, "https:")).searchParams.get("secret") || undefined : undefined);
      const backupCodes = Array.isArray(payload.backupCodes) ? (payload.backupCodes as string[]) : undefined;
      setTwoFASetup({ totpURI, secret, backupCodes });
      if (!totpURI && !secret) {
        // on n'a rien reconnu : on affiche le brut pour permettre à l'utilisateur de lire la réponse
        setTwoFARaw(JSON.stringify(r, null, 2));
      } else {
        setTwoFARaw(null);
      }
      setTwoFAPassword("");
      notify(null, "Activation initiée. Scannez le QR / secret puis validez avec un code TOTP.");
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), null);
    } finally {
      setTwoFABusy(false);
    }
  }

  async function disable2FA() {
    setErr(null);
    setOk(null);
    if (!twoFAPassword()) {
      notify("Mot de passe requis pour désactiver la 2FA.", null);
      return;
    }
    setTwoFABusy(true);
    try {
      await jsend("/api/auth/two-factor/disable", { password: twoFAPassword() });
      setTwoFAPassword("");
      setTwoFASetup(null);
      setTwoFARaw(null);
      notify(null, "2FA désactivée.");
      refetchSession();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), null);
    } finally {
      setTwoFABusy(false);
    }
  }

  async function verify2FA() {
    setErr(null);
    setOk(null);
    if (!twoFACode().trim()) {
      notify("Code TOTP requis.", null);
      return;
    }
    setTwoFABusy(true);
    try {
      await jsend("/api/auth/two-factor/verify-totp", { code: twoFACode().trim() });
      setTwoFACode("");
      setTwoFASetup(null);
      setTwoFARaw(null);
      notify(null, "Code vérifié. 2FA activée.");
      refetchSession();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), null);
    } finally {
      setTwoFABusy(false);
    }
  }

  const user = () => session()?.user || null;
  const provider = () => session()?.session?.provider || (user()?.provider as string | undefined) || "—";

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Réglages
          <span class="plugins-console-sub">profil, mot de passe, 2FA</span>
        </span>
      </div>

      <Show when={err()}>
        <div style={bannerError}>{err()}</div>
      </Show>
      <Show when={ok()}>
        <div style={bannerOk}>{ok()}</div>
      </Show>

      {/* ── Section Mon compte ─────────────────────────────────────── */}
      <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
        <div style={sectionTitleStyle}>Mon compte</div>
        <Show
          when={user()}
          fallback={
            <div style={{ padding: "12px", color: "var(--text-4, #64748b)", "font-size": "12px" }}>
              {session.loading ? "Chargement de la session…" : "Session indisponible."}
            </div>
          }
        >
          {(u) => (
            <div style={{ padding: "8px 0" }}>
              <div style={kvRow}>
                <span style={kvKey}>Email</span>
                <span style={{ color: "var(--text-1, #e8ecf3)" }}>{u().email || "—"}</span>
              </div>
              <div style={kvRow}>
                <span style={kvKey}>Nom</span>
                <span style={{ color: "var(--text-1, #e8ecf3)" }}>{u().name || "—"}</span>
              </div>
              <div style={kvRow}>
                <span style={kvKey}>Rôles</span>
                <span>
                  <Show
                    when={(u().roles || []).length > 0}
                    fallback={<span style={{ color: "var(--text-4)" }}>aucun</span>}
                  >
                    <For each={u().roles || []}>
                      {(r) => (
                        <span class="tag" style={{ "margin-right": "4px" }}>
                          {r}
                        </span>
                      )}
                    </For>
                  </Show>
                </span>
              </div>
              <div style={kvRow}>
                <span style={kvKey}>Tenant</span>
                <span style={{ "font-family": "var(--font-mono)", "font-size": "12px", color: "var(--text-2)" }}>
                  {u().tenantId ? String(u().tenantId).slice(0, 8) : "— Plateforme —"}
                </span>
              </div>
              <div style={kvRow}>
                <span style={kvKey}>Provider</span>
                <span class="tag">{provider()}</span>
              </div>
              <div style={kvRow}>
                <span style={kvKey}>2FA</span>
                <Show
                  when={u().twoFactorEnabled}
                  fallback={
                    <span class="tag" style={{ background: "rgba(148,163,184,0.15)", color: "var(--text-3)" }}>
                      désactivée
                    </span>
                  }
                >
                  <span class="tag" style={{ background: "rgba(34,197,94,0.15)", color: "var(--ok, #22c55e)" }}>
                    activée
                  </span>
                </Show>
              </div>
              <div style={{ padding: "10px 12px", display: "flex", gap: "8px", "flex-wrap": "wrap" }}>
                <button class="btn btn--xs" type="button" onClick={() => refetchSession()}>
                  ↺ Rafraîchir
                </button>
                <button class="btn btn--xs btn--danger" type="button" onClick={signOut}>
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </Show>
      </div>

      {/* ── Section Changer le mot de passe ────────────────────────── */}
      <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
        <div style={sectionTitleStyle}>Changer le mot de passe</div>
        <div
          style={{
            padding: "12px",
            display: "grid",
            "grid-template-columns": "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "10px",
          }}
        >
          <div>
            <label style={labelStyle}>Mot de passe actuel</label>
            <input
              style={{ ...inputStyle, width: "100%" }}
              type="password"
              autocomplete="current-password"
              value={pwdCurrent()}
              onInput={(e) => setPwdCurrent(e.currentTarget.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Nouveau mot de passe (≥ 8 caractères)</label>
            <input
              style={{ ...inputStyle, width: "100%" }}
              type="password"
              autocomplete="new-password"
              value={pwdNew()}
              onInput={(e) => setPwdNew(e.currentTarget.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Confirmer le nouveau mot de passe</label>
            <input
              style={{ ...inputStyle, width: "100%" }}
              type="password"
              autocomplete="new-password"
              value={pwdConfirm()}
              onInput={(e) => setPwdConfirm(e.currentTarget.value)}
            />
          </div>
        </div>
        <div
          style={{ padding: "0 12px 12px", display: "flex", gap: "12px", "align-items": "center", "flex-wrap": "wrap" }}
        >
          <label
            style={{
              display: "flex",
              gap: "6px",
              "align-items": "center",
              "font-size": "12px",
              color: "var(--text-3)",
            }}
          >
            <input type="checkbox" checked={pwdRevoke()} onChange={(e) => setPwdRevoke(e.currentTarget.checked)} />
            Révoquer mes autres sessions
          </label>
          <div style={{ flex: "1 1 0" }} />
          <button class="btn btn--primary btn--xs" type="button" disabled={pwdBusy()} onClick={submitChangePassword}>
            {pwdBusy() ? "…" : "Mettre à jour le mot de passe"}
          </button>
        </div>
      </div>

      {/* ── Section 2FA ────────────────────────────────────────────── */}
      <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
        <div style={sectionTitleStyle}>Authentification à deux facteurs (TOTP)</div>

        <Show
          when={user()?.twoFactorEnabled}
          fallback={
            <div style={{ padding: "12px", display: "flex", "flex-direction": "column", gap: "10px" }}>
              <div style={{ "font-size": "12px", color: "var(--text-3)" }}>
                La 2FA est désactivée. Activez-la pour exiger un code TOTP en plus du mot de passe à chaque connexion.
              </div>
              <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap", "align-items": "flex-end" }}>
                <div>
                  <label style={labelStyle}>Confirmer avec votre mot de passe</label>
                  <input
                    style={inputStyle}
                    type="password"
                    autocomplete="current-password"
                    value={twoFAPassword()}
                    onInput={(e) => setTwoFAPassword(e.currentTarget.value)}
                  />
                </div>
                <button class="btn btn--primary btn--xs" type="button" disabled={twoFABusy()} onClick={enable2FA}>
                  {twoFABusy() ? "…" : "Activer la 2FA"}
                </button>
              </div>

              <Show when={twoFASetup()}>
                {(setup) => (
                  <div
                    style={{
                      background: "rgba(56,189,248,0.08)",
                      border: "1px solid var(--info, #38bdf8)",
                      padding: "10px 12px",
                      "border-radius": "4px",
                      display: "flex",
                      "flex-direction": "column",
                      gap: "8px",
                    }}
                  >
                    <div style={{ "font-size": "12px", color: "var(--text-2)" }}>
                      Scannez ce QR ou copiez le secret dans votre application TOTP (Google Authenticator, Authy,
                      1Password, etc.).
                    </div>
                    <Show when={setup().totpURI}>
                      <div>
                        <div style={labelStyle}>otpauth URI</div>
                        <code
                          style={{
                            display: "block",
                            "font-family": "var(--font-mono)",
                            "font-size": "11px",
                            background: "var(--bg-2, #1e293b)",
                            padding: "6px 8px",
                            "border-radius": "3px",
                            overflow: "auto",
                            "white-space": "nowrap",
                          }}
                        >
                          {setup().totpURI}
                        </code>
                        <button
                          class="btn btn--xs"
                          type="button"
                          style={{ "margin-top": "4px" }}
                          onClick={() => navigator.clipboard?.writeText(setup().totpURI || "")}
                        >
                          Copier l'URI
                        </button>
                      </div>
                    </Show>
                    <Show when={setup().secret}>
                      <div>
                        <div style={labelStyle}>Secret (base32)</div>
                        <code
                          style={{
                            display: "block",
                            "font-family": "var(--font-mono)",
                            "font-size": "13px",
                            background: "var(--bg-2, #1e293b)",
                            padding: "6px 8px",
                            "border-radius": "3px",
                            "letter-spacing": "1px",
                          }}
                        >
                          {setup().secret}
                        </code>
                        <button
                          class="btn btn--xs"
                          type="button"
                          style={{ "margin-top": "4px" }}
                          onClick={() => navigator.clipboard?.writeText(setup().secret || "")}
                        >
                          Copier le secret
                        </button>
                      </div>
                    </Show>
                    <Show when={(setup().backupCodes || []).length > 0}>
                      <div>
                        <div style={labelStyle}>Codes de secours (à stocker)</div>
                        <div
                          style={{
                            display: "grid",
                            "grid-template-columns": "repeat(auto-fill, minmax(120px, 1fr))",
                            gap: "4px",
                            "font-family": "var(--font-mono)",
                            "font-size": "12px",
                          }}
                        >
                          <For each={setup().backupCodes || []}>
                            {(c) => (
                              <code
                                style={{
                                  background: "var(--bg-2, #1e293b)",
                                  padding: "4px 6px",
                                  "border-radius": "3px",
                                  "text-align": "center",
                                }}
                              >
                                {c}
                              </code>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>

                    <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap", "align-items": "flex-end" }}>
                      <div>
                        <label style={labelStyle}>Code TOTP de vérification</label>
                        <input
                          style={inputStyle}
                          type="text"
                          inputmode="numeric"
                          autocomplete="one-time-code"
                          placeholder="123 456"
                          value={twoFACode()}
                          onInput={(e) => setTwoFACode(e.currentTarget.value)}
                        />
                      </div>
                      <button class="btn btn--primary btn--xs" type="button" disabled={twoFABusy()} onClick={verify2FA}>
                        Vérifier et activer
                      </button>
                    </div>
                  </div>
                )}
              </Show>

              <Show when={twoFARaw()}>
                <div>
                  <div style={labelStyle}>Réponse brute (format non reconnu)</div>
                  <pre
                    style={{
                      "font-family": "var(--font-mono)",
                      "font-size": "11px",
                      background: "var(--bg-2, #1e293b)",
                      padding: "8px",
                      "border-radius": "3px",
                      overflow: "auto",
                      "max-height": "200px",
                      color: "var(--text-2)",
                    }}
                  >
                    {twoFARaw()}
                  </pre>
                </div>
              </Show>
            </div>
          }
        >
          <div style={{ padding: "12px", display: "flex", "flex-direction": "column", gap: "10px" }}>
            <div style={{ "font-size": "12px", color: "var(--text-3)" }}>
              La 2FA est actuellement activée pour votre compte.
            </div>
            <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap", "align-items": "flex-end" }}>
              <div>
                <label style={labelStyle}>Confirmer avec votre mot de passe</label>
                <input
                  style={inputStyle}
                  type="password"
                  autocomplete="current-password"
                  value={twoFAPassword()}
                  onInput={(e) => setTwoFAPassword(e.currentTarget.value)}
                />
              </div>
              <button class="btn btn--xs btn--danger" type="button" disabled={twoFABusy()} onClick={disable2FA}>
                {twoFABusy() ? "…" : "Désactiver la 2FA"}
              </button>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
